import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getBlogPost, type BlogBlock } from "@/data/blogPosts";
import { useScrollDepth, trackEvent } from "@/lib/tracking";
import { SITE } from "@/lib/site";
import { facilitySrcSet } from "@/lib/responsiveImage";

// Text blocks. In-body images are handled by renderImage (alternating float) so
// long posts read like an article, not a stack of full-width blocks.
function renderBlock(block: BlogBlock, i: number) {
  switch (block.type) {
    case "h2":
      return (
        <h2 key={i} className="clear-none font-instrument text-3xl md:text-4xl text-[hsl(var(--velto-forest))] mt-14 mb-5 leading-tight">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="font-instrument text-2xl text-[hsl(var(--velto-forest))] mt-10 mb-3">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p key={i} className="text-foreground/85 text-base md:text-lg leading-relaxed mb-6">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={i} className="list-disc pl-6 mb-7 space-y-2.5 text-foreground/85 text-base md:text-lg leading-relaxed marker:text-[hsl(var(--velto-copper))]">
          {block.items.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol key={i} className="list-decimal pl-6 mb-7 space-y-2.5 text-foreground/85 text-base md:text-lg leading-relaxed marker:text-[hsl(var(--velto-copper))]">
          {block.items.map((it, j) => <li key={j}>{it}</li>)}
        </ol>
      );
    case "quote":
      return (
        <blockquote key={i} className="border-l-4 border-[hsl(var(--velto-copper))] pl-4 italic text-foreground/80 my-8 text-base md:text-lg">
          {block.text}
        </blockquote>
      );
    case "img":
      return null; // rendered by renderImage() in the body map
  }
}

// In-body image: a large, full-width rounded block between paragraphs — stacked,
// never floated, spanning the reading column. width/height reserve the aspect box
// so it is CLS-neutral; lazy-loaded since these sit below the fold.
function renderImage(block: Extract<BlogBlock, { type: "img" }>, i: number) {
  return (
    <figure key={i} className="my-10">
      <img
        src={block.src}
        srcSet={block.srcSet}
        // Full content-column width (~700px on desktop, full-width below), so the
        // browser picks a 2x-sharp variant for the large slot.
        sizes="(min-width: 1024px) 700px, 100vw"
        alt={block.alt}
        width={block.width}
        height={block.height}
        loading="lazy"
        decoding="async"
        className="w-full h-auto rounded-2xl"
      />
      {block.caption && (
        <figcaption className="mt-2 text-sm italic text-muted-foreground">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  useScrollDepth([50, 75], "blog_engagement", { slug: slug ?? "" });

  useEffect(() => {
    if (post) trackEvent("blog_post_view", { slug: post.slug });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `${SITE}/blog/${post.slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: `${SITE}${post.heroImage}`,
    datePublished: post.publishedDate,
    dateModified: post.modifiedDate,
    author: { "@type": "Organization", name: post.author, url: SITE },
    publisher: {
      "@type": "Organization",
      name: "Velto Pickleball Club",
      logo: { "@type": "ImageObject", url: `${SITE}/Velto_04_Crest_Forest.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  };

  const faqJsonLd =
    post.faqs && post.faqs.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
      : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={canonical} />
        {!post.indexable && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`${SITE}${post.heroImage}`} />
        <meta property="article:published_time" content={post.publishedDate} />
        <meta property="article:modified_time" content={post.modifiedDate} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={`${SITE}${post.heroImage}`} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
      </Helmet>

      <Header />

      <article className="max-w-3xl mx-auto px-6 pt-banner-8 pb-24">
        <nav aria-label="Breadcrumb" className="max-w-[70ch] text-sm mb-6 text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link> <span>/</span>{" "}
          <Link to="/blog" className="hover:underline">Blog</Link> <span>/</span>{" "}
          <span>{post.title}</span>
        </nav>

        <header className="mb-12 max-w-[70ch]">
          <time className="text-xs uppercase tracking-wider text-muted-foreground" dateTime={post.publishedDate}>
            {new Date(post.publishedDate).toLocaleDateString("en-US", {
              year: "numeric", month: "long", day: "numeric",
            })}{" "}
            · {post.author}
          </time>
          <h1 className="font-instrument text-4xl md:text-5xl text-[hsl(var(--velto-forest))] mt-3 leading-tight">
            {post.title}
          </h1>

          {/* Stacked opening: lead paragraph, then a large full-width hero banner. */}
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-foreground/80">
            {post.excerpt}
          </p>
          <img
            src={post.heroImage}
            srcSet={post.heroSrcSet ?? facilitySrcSet(post.heroImage)}
            sizes="(min-width: 1024px) 700px, 100vw"
            alt={post.heroImageAlt}
            width={1200}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="mt-8 w-full aspect-[3/2] object-cover rounded-2xl"
          />
        </header>

        <div className="max-w-[70ch]">
          {post.body.map((block, i) =>
            block.type === "img" ? renderImage(block, i) : renderBlock(block, i)
          )}

          {post.faqs && post.faqs.length > 0 && (
            <section className="mt-14">
              <h2 className="font-instrument text-3xl md:text-4xl text-[hsl(var(--velto-forest))] mb-5">
                Frequently asked
              </h2>
              <dl className="space-y-5">
                {post.faqs.map((f, i) => (
                  <div key={i}>
                    <dt className="font-semibold text-[hsl(var(--velto-forest))]">{f.question}</dt>
                    <dd className="mt-1 text-foreground/85 text-base md:text-lg leading-relaxed">{f.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>

        {post.relatedLinks.length > 0 && (
          <aside className="clear-both mt-16 pt-8 border-t border-border max-w-[70ch]">
            <h2 className="font-instrument text-2xl text-[hsl(var(--velto-forest))] mb-4">
              Related at Velto
            </h2>
            <ul className="space-y-2">
              {post.relatedLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-[hsl(var(--velto-copper))] hover:underline"
                  >
                    {l.label} →
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;